import React from 'react';
import Header from '../../components/ui/Header';
import DonationQuickAction from '../../components/ui/DonationQuickAction';
import CharityHeader from './components/CharityHeader';
import FinancialTransparency from './components/FinancialTransparency';
import ImpactStories from './components/ImpactStories';
import DonationSection from './components/DonationSection';
import RecentActivity from './components/RecentActivity';
import OrganizationDetails from './components/OrganizationDetails';

const CharityProfile = () => {
  // Mock charity data
  const charityData = {
    id: 1,
    name: "Clean Water Initiative",
    mission: "Providing clean, safe drinking water to underserved communities worldwide through sustainable infrastructure projects and community education programs.",
    location: "San Francisco, CA",
    coverImage: "https://images.unsplash.com/photo-1594736797933-d0401ba2fe65?w=800&h=400&fit=crop",
    logo: "https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=100&h=100&fit=crop",
    verificationBadges: ["501(c)(3) Verified", "GuideStar Gold", "Charity Navigator 4-Star"],
    stats: {
      totalDonations: "$2.4M",
      beneficiaries: "50K+",
      projects: "127",
      transparencyScore: 94
    }
  };

  const financialData = {
    transparencyScore: 94,
    allocation: [
      { category: "programs", value: 85 },
      { category: "administrative", value: 8 },
      { category: "fundraising", value: 5 },
      { category: "reserves", value: 2 }
    ],
    revenueHistory: [
      { year: "2020", amount: 1200000 },
      { year: "2021", amount: 1800000 },
      { year: "2022", amount: 2100000 },
      { year: "2023", amount: 2400000 },
      { year: "2024", amount: 2800000 }
    ],
    reports: [
      { id: 1, title: "Annual Report 2023", year: "2023", pages: 45 },
      { id: 2, title: "Financial Statement 2023", year: "2023", pages: 12 },
      { id: 3, title: "Impact Report 2023", year: "2023", pages: 28 },
      { id: 4, title: "Annual Report 2022", year: "2022", pages: 42 }
    ]
  };

  const impactStories = [
    {
      id: 1,
      title: "Maria\'s Village Gets Clean Water",
      location: "Guatemala",
      date: "March 2024",
      image: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=400&h=300&fit=crop",
      excerpt: "After years without access to clean water, Maria's village now has a sustainable well system serving 200 families.",
      fullStory: `Maria Rodriguez had to walk 3 miles every day to collect water for her family. The nearest clean water source was a dangerous journey through rough terrain, and the water quality was often questionable.\n\nThanks to the generous donations from our community, we were able to install a solar-powered well system in Maria's village. The project took 6 months to complete and now serves over 200 families with clean, safe drinking water.\n\n"I can't believe we have clean water right here in our village," says Maria. "My children can now go to school instead of helping me carry water, and we don't get sick as often."\n\nThe well system includes a water treatment facility and storage tanks that can hold enough water for the entire village for a week. Local community members were trained to maintain the system, ensuring its long-term sustainability.`,
      impact: "200 families now have access to clean water, reducing waterborne illness by 85% and allowing 150 children to attend school regularly."
    },
    {
      id: 2,
      title: "School Water Program Success",location: "Kenya",date: "February 2024",image: "https://images.unsplash.com/photo-1497486751825-1233686d5d80?w=400&h=300&fit=crop",excerpt: "Installing water filtration systems in 12 schools has improved health outcomes for over 3,000 students.",
      fullStory: `The Kibera School Water Program was launched in response to the high rates of waterborne diseases among students. Many children were missing school due to illness caused by contaminated water sources.\n\nOur team worked with local schools to install advanced water filtration systems that can purify up to 1,000 gallons of water per day. Each system includes multiple filtration stages and UV sterilization.\n\nThe results have been remarkable. School attendance has increased by 40%, and reported cases of waterborne illness have dropped by 90%. Teachers report that students are more focused and energetic in class.\n\n"The difference is night and day," says Principal Sarah Wanjiku. "Our students are healthier, happier, and learning better than ever before."`,impact: "3,000 students across 12 schools now have access to clean water, with a 90% reduction in waterborne illness and 40% increase in attendance."
    },
    {
      id: 3,
      title: "Emergency Water Relief",location: "Philippines",date: "January 2024",image: "https://images.unsplash.com/photo-1547036967-23d11aacaee0?w=400&h=300&fit=crop",excerpt: "Rapid response team provided emergency water supplies to 5,000 people affected by flooding.",
      fullStory: `When devastating floods hit the Philippines in January 2024, our emergency response team was on the ground within 48 hours. The floods had contaminated local water supplies, leaving thousands without access to safe drinking water.\n\nOur team deployed portable water purification units and distributed emergency water supplies to affected communities. We also set up temporary water distribution points in evacuation centers.\n\nWorking with local authorities and volunteers, we were able to provide clean water to over 5,000 people during the critical first weeks after the disaster. Our efforts helped prevent a potential health crisis in the affected areas.\n\n"When everything was underwater, having clean water meant everything to us," says flood survivor Carlos Santos. "It gave us hope that we could rebuild."`,impact: "5,000 people received emergency clean water supplies, preventing waterborne disease outbreaks in flood-affected areas."
    }
  ];

  const recentActivities = [
    {
      id: 1,
      type: "donations",
      user: "Anonymous Donor",
      description: "made a generous donation",
      amount: 5000,
      timestamp: new Date(Date.now() - 1800000), // 30 minutes ago
      actions: [
        { icon: "Heart", label: "Thank" },
        { icon: "Share", label: "Share" }
      ]
    },
    {
      id: 2,
      type: "milestones",
      description: "reached 50,000 beneficiaries milestone!",
      details: "Thanks to your support, we've now provided clean water access to over 50,000 people worldwide.",
      timestamp: new Date(Date.now() - 7200000), // 2 hours ago
      image: "https://images.unsplash.com/photo-1559827260-dc66d52bef19?w=400&h=200&fit=crop",
      actions: [
        { icon: "Heart", label: "Celebrate" },
        { icon: "Share", label: "Share" }
      ]
    },
    {
      id: 3,
      type: "donations",
      user: "Sarah Johnson",
      description: "started a monthly recurring donation",
      amount: 100,
      details: "Monthly donation to support ongoing water projects",
      timestamp: new Date(Date.now() - 14400000), // 4 hours ago
      actions: [
        { icon: "Heart", label: "Thank" },
        { icon: "MessageCircle", label: "Message" }
      ]
    },
    {
      id: 4,
      type: "updates",
      description: "published a new impact report",
      details: "Q1 2024 Impact Report: See how your donations are making a difference",
      timestamp: new Date(Date.now() - 86400000), // 1 day ago
      actions: [
        { icon: "Download", label: "Download" },
        { icon: "Share", label: "Share" }
      ]
    },
    {
      id: 5,
      type: "donations",
      user: "Michael Chen",
      description: "made a donation",
      amount: 250,
      timestamp: new Date(Date.now() - 172800000), // 2 days ago
      actions: [
        { icon: "Heart", label: "Thank" },
        { icon: "Share", label: "Share" }
      ]
    },
    {
      id: 6,
      type: "updates",
      description: "completed water well installation in Guatemala",
      details: "New well now serves 200 families with clean water access",
      timestamp: new Date(Date.now() - 259200000), // 3 days ago
      image: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=400&h=200&fit=crop",
      actions: [
        { icon: "Heart", label: "Celebrate" },
        { icon: "Share", label: "Share" }
      ]
    }
  ];

  const organizationData = {
    mission: "Providing clean, safe drinking water to underserved communities worldwide through sustainable infrastructure projects and community education programs.",
    vision: "A world where every person has access to clean, safe drinking water and proper sanitation facilities, enabling healthier communities and brighter futures for all.",
    history: `Founded in 2015 by a group of engineers and humanitarian workers, Clean Water Initiative began as a small project to bring clean water to a single village in Guatemala. What started as a local effort has grown into a global movement, with projects spanning across 15 countries and impacting over 50,000 lives.\n\nOur approach combines cutting-edge water purification technology with community-based solutions, ensuring that every project is sustainable and locally managed. We believe that access to clean water is a fundamental human right, and we work tirelessly to make this vision a reality.`,
    founded: "2015",
    taxId: "12-3456789",
    legalStatus: "501(c)(3) Non-profit Organization",
    registration: "California Secretary of State",
    leadership: [
      {
        id: 1,
        name: "Dr. Emily Rodriguez",
        position: "Executive Director",
        photo: "https://randomuser.me/api/portraits/women/44.jpg",
        bio: "Dr. Rodriguez brings over 15 years of experience in international development and water engineering. She holds a PhD in Environmental Engineering from Stanford University.",
        credentials: ["PhD Environmental Engineering", "PE License", "PMP Certified"]
      },
      {
        id: 2,
        name: "James Thompson",
        position: "Director of Operations",
        photo: "https://randomuser.me/api/portraits/men/32.jpg",
        bio: "James oversees all field operations and project implementation. His background includes 12 years in humanitarian logistics and emergency response.",
        credentials: ["MS International Development", "WASH Specialist", "Emergency Response Certified"]
      },
      {
        id: 3,
        name: "Maria Santos",
        position: "Chief Financial Officer",
        photo: "https://randomuser.me/api/portraits/women/28.jpg",
        bio: "Maria ensures financial transparency and accountability. She is a CPA with extensive experience in non-profit financial management.",
        credentials: ["CPA", "MBA Finance", "Non-profit Management Certificate"]
      },
      {
        id: 4,
        name: "Dr. Ahmed Hassan",
        position: "Technical Director",
        photo: "https://randomuser.me/api/portraits/men/45.jpg",
        bio: "Dr. Hassan leads our technical team and oversees water system design and implementation. He has 20 years of experience in water treatment technology.",
        credentials: ["PhD Water Resources", "Professional Engineer", "AWWA Member"]
      }
    ],
    programs: [
      {
        id: 1,
        name: "Village Water Systems",
        description: "Installing sustainable water wells and treatment systems in rural communities",
        beneficiaries: "35,000+",
        budget: 1800000,
        duration: "Ongoing",
        status: "Active",
        image: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?w=400&h=200&fit=crop"
      },
      {
        id: 2,
        name: "School WASH Program",
        description: "Providing water, sanitation, and hygiene facilities in schools",
        beneficiaries: "12,000+",
        budget: 650000,
        duration: "2 years",
        status: "Active",
        image: "https://images.unsplash.com/photo-1497486751825-1233686d5d80?w=400&h=200&fit=crop"
      },
      {
        id: 3,
        name: "Emergency Response",
        description: "Rapid deployment of water purification systems during disasters",
        beneficiaries: "8,000+",
        budget: 400000,
        duration: "As needed",
        status: "Active",
        image: "https://images.unsplash.com/photo-1547036967-23d11aacaee0?w=400&h=200&fit=crop"
      }
    ],
    contact: {
      address: "123 Mission Street\nSuite 400\nSan Francisco, CA 94105\nUnited States",
      phone: "+1 (555) 123-4567",
      email: "info@cleanwaterinitiative.org",
      website: "https://www.cleanwaterinitiative.org",
      coordinates: {
        lat: 37.7749,
        lng: -122.4194
      },
      hours: [
        { days: "Monday - Friday", time: "9:00 AM - 6:00 PM" },
        { days: "Saturday", time: "10:00 AM - 4:00 PM" },
        { days: "Sunday", time: "Closed" }
      ],
      socialMedia: [
        { platform: "Facebook", url: "https://facebook.com/cleanwaterinitiative", icon: "Facebook" },
        { platform: "Twitter", url: "https://twitter.com/cleanwaterorg", icon: "Twitter" },
        { platform: "Instagram", url: "https://instagram.com/cleanwaterinitiative", icon: "Instagram" },
        { platform: "LinkedIn", url: "https://linkedin.com/company/cleanwaterinitiative", icon: "Linkedin" }
      ]
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 lg:px-6 py-8">
        <div className="space-y-8">
          {/* Charity Header */}
          <CharityHeader charity={charityData} />

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Left Column - Main Content */}
            <div className="lg:col-span-2 space-y-8">
              <FinancialTransparency financialData={financialData} />
              <ImpactStories stories={impactStories} />
              <OrganizationDetails organization={organizationData} />
              <RecentActivity activities={recentActivities} />
            </div>

            {/* Right Column - Donation Section */}
            <div className="lg:col-span-1">
              <div className="sticky top-24">
                <DonationSection charity={charityData} />
              </div>
            </div>
          </div>
        </div>
      </main>

      <DonationQuickAction />
    </div>
  );
};

export default CharityProfile;